import { useState, useEffect } from "react";
import { MainLayout } from "../components/Layout";
import { adminAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, Edit, Trash2, Shield, Key, UserCheck, UserX, Crown , Link, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERMISSION_LIST = [
  { key: "clients", label: "Gestion des clients" },
  { key: "repairs", label: "Gestion des réparations" },
  { key: "inventory", label: "Gestion du stock" },
  { key: "invoices", label: "Facturation et devis" },
  { key: "reviews", label: "Gestion des avis" },
  { key: "dashboard", label: "Tableau de bord" }
];

function getPermLabel(key) {
  for (let i = 0; i < PERMISSION_LIST.length; i++) {
    if (PERMISSION_LIST[i].key === key) return PERMISSION_LIST[i].label;
  }
  return key;
}

function EmployeeCard({ employee, onEdit, onDelete, onToggle, onResetPassword }) {
  const initial = employee.name && employee.name[0] ? employee.name[0].toUpperCase() : '?';
  const isAdmin = employee.role === 'admin';
  
  return (
    <Card className={"bg-card border-border card-hover " + (employee.is_active ? '' : 'opacity-60')}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={"w-10 h-10 rounded-full flex items-center justify-center " + (isAdmin ? 'bg-amber-500/20' : employee.is_active ? 'bg-primary/20' : 'bg-muted')}>
              {isAdmin ? (
                <Crown className="w-5 h-5 text-amber-500" />
              ) : (
                <span className={"text-sm font-medium " + (employee.is_active ? 'text-primary' : 'text-muted-foreground')}>
                  {initial}
                </span>
              )}
            </div>
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                {employee.name}
                {isAdmin && (
                  <span className="px-2 py-0.5 bg-amber-500/20 text-amber-500 text-xs rounded-full font-medium">
                    Admin
                  </span>
                )}
              </CardTitle>
              <p className="text-xs text-muted-foreground font-mono">{employee.email}</p>
            </div>
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => onToggle(employee)}
              className={"p-2 rounded-md transition-colors duration-150 " + (employee.is_active ? 'hover:bg-amber-500/10' : 'hover:bg-accent/10')}
              title={employee.is_active ? "Désactiver" : "Activer"}
            >
              {employee.is_active ? (
                <UserX className="w-4 h-4 text-amber-500" />
              ) : (
                <UserCheck className="w-4 h-4 text-accent" />
              )}
            </button>
            <button
              onClick={() => onResetPassword(employee.id)}
              className="p-2 hover:bg-secondary rounded-md transition-colors duration-150"
              title="Réinitialiser le mot de passe"
            >
              <Key className="w-4 h-4 text-muted-foreground" />
            </button>
            <button
              onClick={() => onEdit(employee)}
              className="p-2 hover:bg-secondary rounded-md transition-colors duration-150"
            >
              <Edit className="w-4 h-4 text-muted-foreground" />
            </button>
            <button
              onClick={() => onDelete(employee.id)}
              className="p-2 hover:bg-destructive/10 rounded-md transition-colors duration-150"
            >
              <Trash2 className="w-4 h-4 text-destructive" />
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2 mb-3">
          <span className={"px-2 py-1 rounded text-xs " + (employee.is_active ? 'bg-accent/20 text-accent' : 'bg-muted text-muted-foreground')}>
            {employee.is_active ? "Actif" : "Désactivé"}
          </span>
        </div>
        {!isAdmin && (
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground mb-2">Permissions:</p>
            <div className="flex flex-wrap gap-1">
              {employee.permissions && employee.permissions.map(function(perm) {
                return (
                  <span key={perm} className="px-2 py-0.5 bg-secondary text-xs rounded">
                    {getPermLabel(perm)}
                  </span>
              );
            })}
          </div>
        </div>
        )}
        {isAdmin && (
          <p className="text-xs text-amber-500/80">Accès complet à toutes les fonctionnalités</p>
        )}
      </CardContent>
    </Card>
  );
}

export default function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  
  const [formFirstName, setFormFirstName] = useState("");
  const [formLastName, setFormLastName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPassword, setFormPassword] = useState("");
  const [formPermissions, setFormPermissions] = useState(["clients", "repairs", "dashboard"]);
  const [formRole, setFormRole] = useState("employee");

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const res = await adminAPI.getEmployees();
      setEmployees(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEmployee) {
        await adminAPI.updateEmployee(editingEmployee.id, {
          name: `${formFirstName} ${formLastName}`.trim(),
          permissions: formPermissions,
          role: formRole
        });
        toast.success(formRole === 'admin' ? "Administrateur mis à jour" : "Employé mis à jour");
      } else {
        await adminAPI.createEmployee({
          name: `${formFirstName} ${formLastName}`.trim(),
          email: formEmail,
          password: formPassword,
          permissions: formPermissions,
          role: formRole
        });
        toast.success(formRole === 'admin' ? "Administrateur créé" : "Employé créé");
      }
      setDialogOpen(false);
      resetForm();
      fetchEmployees();
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.detail;
      toast.error(msg || "Erreur");
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    const nameParts = (employee.name || "").split(" ");
    setFormFirstName(nameParts[0] || "");
    setFormLastName(nameParts.slice(1).join(" ") || "");
    setFormEmail(employee.email);
    setFormPassword("");
    setFormPermissions(employee.permissions || []);
    setFormRole(employee.role || "employee");
    setDialogOpen(true);
  };

  const handleAttach = async (userId) => {
    if (!window.confirm("Rattacher ce compte à votre magasin ? Il verra toutes vos données.")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_URL}/api/admin/employees/${userId}/attach-to-shop`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Compte rattaché au magasin — l'utilisateur voit maintenant toutes vos données");
      fetchEmployees();
    } catch (err) {
      toast.error("Erreur lors du rattachement");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cet employé ?")) return;
    try {
      await adminAPI.deleteEmployee(id);
      toast.success("Employé supprimé");
      fetchEmployees();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleToggleActive = async (employee) => {
    try {
      await adminAPI.updateEmployee(employee.id, { is_active: !employee.is_active });
      toast.success(employee.is_active ? "Compte désactivé" : "Compte activé");
      fetchEmployees();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleResetPasswordClick = (id) => {
    setSelectedEmployeeId(id);
    setPasswordDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    try {
      await adminAPI.resetPassword(selectedEmployeeId, newPassword);
      toast.success("Mot de passe réinitialisé");
      setPasswordDialogOpen(false);
      setNewPassword("");
      setSelectedEmployeeId(null);
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const resetForm = () => {
    setEditingEmployee(null);
    setFormFirstName("");
    setFormLastName("");
    setFormEmail("");
    setFormPassword("");
    setFormPermissions(["clients", "repairs", "dashboard"]);
    setFormRole("employee");
  };

  const togglePermission = (perm) => {
    // dashboard is mandatory - cannot be removed
    if (perm === "dashboard" && formPermissions.indexOf("dashboard") !== -1) {
      toast.warning("La permission 'Tableau de bord' est obligatoire pour chaque employé");
      return;
    }
    const idx = formPermissions.indexOf(perm);
    if (idx === -1) {
      setFormPermissions([...formPermissions, perm]);
    } else {
      const newPerms = [];
      for (let i = 0; i < formPermissions.length; i++) {
        if (formPermissions[i] !== perm) {
          newPerms.push(formPermissions[i]);
        }
      }
      setFormPermissions(newPerms);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="employees-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-chivo text-3xl font-bold text-foreground">Comptes & accès</h1>
            <p className="text-muted-foreground mt-1">
              {employees.filter(e => e.role === 'admin').length} admin(s) · {employees.filter(e => e.role !== 'admin').length} employé(s)
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={function(open) { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-employee-btn" className="bg-primary hover:bg-primary/90 btn-animate">
                <Plus className="w-4 h-4 mr-2" />
                Nouvel employé
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-chivo">
                  {editingEmployee ? "Modifier l'employé" : "Nouvel employé"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Prénom *</Label>
                    <Input
                      value={formFirstName}
                      onChange={function(e) { setFormFirstName(e.target.value); }}
                      required
                      placeholder="Jean"
                      data-testid="employee-firstname-input"
                      className="bg-secondary/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nom *</Label>
                    <Input
                      value={formLastName}
                      onChange={function(e) { setFormLastName(e.target.value); }}
                      required
                      placeholder="Dupont"
                      data-testid="employee-lastname-input"
                      className="bg-secondary/50"
                    />
                  </div>
                </div>
                
                {!editingEmployee && (
                  <>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={formEmail}
                        onChange={function(e) { setFormEmail(e.target.value); }}
                        required
                        data-testid="employee-email-input"
                        className="bg-secondary/50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Mot de passe *</Label>
                      <Input
                        type="password"
                        value={formPassword}
                        onChange={function(e) { setFormPassword(e.target.value); }}
                        required
                        minLength={6}
                        data-testid="employee-password-input"
                        className="bg-secondary/50"
                      />
                    </div>
                  </>
                )}
                
                <div className="space-y-2">
                  <Label>Rôle *</Label>
                  <Select value={formRole} onValueChange={setFormRole}>
                    <SelectTrigger className="bg-secondary/50">
                      <SelectValue placeholder="Sélectionner un rôle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4" />
                          <span>Employé</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="admin">
                        <div className="flex items-center gap-2">
                          <Crown className="w-4 h-4 text-amber-500" />
                          <span>Administrateur</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {formRole === 'admin' && (
                    <p className="text-xs text-amber-500 mt-1">
                      Les administrateurs ont accès à toutes les fonctionnalités
                    </p>
                  )}
                </div>
                
                {formRole === 'employee' && (
                  <div className="space-y-3">
                    <Label>Permissions</Label>
                    <div className="grid grid-cols-1 gap-3 p-4 bg-secondary/30 rounded-md">
                      {PERMISSION_LIST.map(function(perm) {
                        const isMandatory = perm.key === "dashboard";
                        return (
                          <div key={perm.key} className="flex items-center space-x-3">
                            <Checkbox
                              id={perm.key}
                              checked={formPermissions.indexOf(perm.key) !== -1}
                              onCheckedChange={function() { togglePermission(perm.key); }}
                              disabled={isMandatory}
                            />
                            <label htmlFor={perm.key} className={"text-sm " + (isMandatory ? "text-muted-foreground cursor-not-allowed" : "cursor-pointer")}>
                              {perm.label}
                              {isMandatory && <span className="ml-2 text-xs text-primary/60">(obligatoire)</span>}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={function() { setDialogOpen(false); resetForm(); }} className="flex-1">
                    Annuler
                  </Button>
                  <Button type="submit" data-testid="employee-submit-btn" className="flex-1 bg-primary hover:bg-primary/90">
                    {editingEmployee ? "Mettre à jour" : "Créer"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Chargement...</div>
        ) : employees.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucun compte pour le moment</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Section comptes orphelins (autre shop_id) */}
            {employees.filter(e => e._orphan).length > 0 && (
              <div className="p-4 rounded-xl border border-amber-500/40 bg-amber-500/5">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  <h2 className="font-semibold text-amber-400">Comptes non rattachés à ce magasin</h2>
                  <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full">
                    {employees.filter(e => e._orphan).length}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                  Ces comptes existent dans la base mais sont isolés (shop_id différent). Ils ne voient pas vos réparations.
                  Cliquez "Rattacher" pour les intégrer à votre magasin.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {employees.filter(e => e._orphan).map(function(employee) {
                    return (
                      <div key={employee.id} className="flex items-center justify-between p-3 rounded-lg bg-card border border-border">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-amber-500/20 flex items-center justify-center">
                            <span className="text-sm font-medium text-amber-400">
                              {employee.name && employee.name[0] ? employee.name[0].toUpperCase() : '?'}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium">{employee.name}</p>
                            <p className="text-xs text-muted-foreground font-mono">{employee.email}</p>
                            <p className="text-xs text-amber-500/70 mt-0.5">
                              {employee.role === 'admin' ? 'Administrateur' : 'Employé'} — shop isolé
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleAttach(employee.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-xs font-medium transition-colors"
                        >
                          <Link className="w-3.5 h-3.5" />
                          Rattacher
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Section Administrateurs */}
            {employees.filter(e => e.role === 'admin' && !e._orphan).length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Crown className="w-4 h-4 text-amber-500" />
                  <h2 className="font-semibold text-foreground">Administrateurs</h2>
                  <span className="text-xs text-muted-foreground bg-amber-500/10 text-amber-400 px-2 py-0.5 rounded-full">
                    {employees.filter(e => e.role === 'admin' && !e._orphan).length}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {employees.filter(e => e.role === 'admin' && !e._orphan).map(function(employee) {
                    return (
                      <EmployeeCard 
                        key={employee.id} 
                        employee={employee} 
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onToggle={handleToggleActive}
                        onResetPassword={handleResetPasswordClick}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Section Employés */}
            {employees.filter(e => e.role !== 'admin' && !e._orphan).length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="w-4 h-4 text-primary" />
                  <h2 className="font-semibold text-foreground">Employés</h2>
                  <span className="text-xs text-muted-foreground bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                    {employees.filter(e => e.role !== 'admin' && !e._orphan).length}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {employees.filter(e => e.role !== 'admin' && !e._orphan).map(function(employee) {
                    return (
                      <EmployeeCard 
                        key={employee.id} 
                        employee={employee} 
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onToggle={handleToggleActive}
                        onResetPassword={handleResetPasswordClick}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-chivo">Réinitialiser le mot de passe</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Nouveau mot de passe</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={function(e) { setNewPassword(e.target.value); }}
                  placeholder="Au moins 6 caractères"
                  data-testid="new-password-input"
                  className="bg-secondary/50"
                />
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={function() { setPasswordDialogOpen(false); setNewPassword(""); }} className="flex-1">
                  Annuler
                </Button>
                <Button onClick={handleResetPassword} data-testid="confirm-reset-password-btn" className="flex-1 bg-primary hover:bg-primary/90">
                  Réinitialiser
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
